// Guided setup copy (shared-docs/handoff_guided_setup/GUIDED_SETUP_BUILD_SPEC.md
// §1): the wizard's welcome, "Your tools", working days, drivers hint and the
// last step's handoff by AI assistant. The assistant answer is only about
// connecting an assistant to Cadence; nothing here may suggest Cadence's own
// AI is off. `{reader}`, `{assistant}`, `{times}` and `{time}` are filled in by
// OnboardingWizardPage.

export const setupCopy = {
  chrome: {
    brand: 'Cadence',
    brandSuffix: '\u00b7 by Stratium',
    skip: 'Skip setup',
    back: 'Back',
    continue: 'Continue',
  },

  calendar: {
    eyebrow: 'Step 1 \u00b7 Your calendar',
    title: 'Start with your day.',
    sub: 'Cadence reads your calendar to see where your time goes, and writes to it so it can hold focus time for you. Nothing is shared.',
    microsoft: 'Microsoft 365',
    microsoftSub: 'Outlook calendar',
    google: 'Google Calendar',
    googleSub: 'Google Workspace',
    connected: 'Connected',
  },

  welcome: {
    title: 'Let’s give you your time back.',
    sub: 'Setup comes in two parts. Cadence walks you through both and checks each step for you.',
    partOneTitle: 'Here, in Cadence · about 3 minutes',
    partOneBody: 'Your calendar, the tools you use, your hours and your focus. You finish with tomorrow already protected.',
    partTwoTitle: 'Then, in your AI assistant · about 10 minutes',
    partTwoBody: 'Connect Claude so it can file actions from your inbox through the day and close your day for you. Do it now, later, or not at all. Cadence works without an assistant too.',
    start: 'Start setup',
  },

  railLabels: {
    calendar: 'Calendar',
    tools: 'Your tools',
    protect: 'Protect',
    focus: 'Focus',
    drivers: 'Drivers',
    build: 'Build',
  },

  tools: {
    eyebrow: 'Step 2 · Your tools',
    title: 'What do you use every day?',
    sub: 'Start with your AI assistant. It reads your tools for you, and Cadence only shows you the setup for what you pick.',
    assistantQuestion: 'Which AI assistant will you use with Cadence?',
    claudePlanQuestion: 'How do you have Claude?',
    toolsQuestion: 'Which tools should {reader} read for you?',
    readerFallback: 'your assistant',
    noneNote: 'Cadence plans your day, protects your focus and sends your morning brief. Connect an assistant any time from Setup to add the action sweep and end-of-day close.',
    notYetNote: 'The action sweep and end-of-day close run in Claude. Everything else in this setup works without it. At the end, Cadence shows you how to get started with Claude.',
    otherNote: 'The action sweep and end-of-day close run in Claude today. {assistant} isn’t supported yet. Everything else in this setup still works, and at the end you can choose what to do next.',
    chooseFirst: 'Choose an assistant to continue.',
  },

  // Keyed by the server's aiClient values; ASSISTANT_ORDER sets the order.
  assistants: {
    claude: { name: 'Claude', sub: 'Free, Pro, Max, Team or Enterprise', tag: 'Full setup' },
    chatgpt: { name: 'ChatGPT', sub: 'Plus, Pro, Business or Enterprise' },
    copilot: { name: 'Microsoft Copilot', sub: 'Microsoft 365 Copilot at work' },
    gemini: { name: 'Gemini', sub: 'Google Workspace or personal' },
    notYet: { name: 'Not yet', sub: 'Show me how to get started' },
    none: { name: 'No AI assistant', sub: 'Use Cadence on its own. Connect one later in Setup.' },
  },

  claudePlans: {
    personal: { name: 'My own plan', sub: 'Pro or Max. You add connectors and skills yourself.' },
    work: { name: 'Through my company', sub: 'Team or Enterprise. Your admin may need to approve a step or two.' },
    free: { name: 'Claude Free', sub: 'You can connect Cadence. The scheduled routines need a paid plan.' },
  },

  toolGroups: {
    email: 'Email and chat',
    meetings: 'Meetings',
    customers: 'Customers',
  },

  // Keyed by the server's source ids; SOURCE_ORDER sets the order.
  sources: {
    m365: { name: 'Microsoft 365', sub: 'Outlook, Teams and meeting transcripts' },
    google: { name: 'Google Workspace', sub: 'Gmail and Google Calendar' },
    slack: { name: 'Slack', sub: 'Channels and direct messages' },
    plaud: { name: 'Plaud', sub: 'Recorded meetings and notes' },
    notes: { name: 'Otter or Fireflies', sub: 'Meeting transcripts' },
    sf: { name: 'Salesforce', sub: 'Accounts, opportunities and pipeline' },
  },

  protect: {
    eyebrow: 'Step 3 \u00b7 Protect what matters',
    title: 'Your time, and your life.',
    sub: 'Cadence defends your personal time like a board meeting.',
    dayStarts: 'Working day starts',
    dayEnds: 'Working day ends',
    schoolRun: 'The school run',
    schoolRunHint: 'Weekday mornings and afternoons, held.',
    lunch: 'Lunch',
    lunchHint: 'A daily protected break.',
    claudeHoursNote: 'Claude’s routines run inside these hours too.',
    workingDays: 'Working days',
    workingDaysHint: 'Cadence plans and protects these days.',
    days: {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    },
  },

  focus: {
    eyebrow: 'Step 4 \u00b7 Your focus rhythm',
    title: 'How much deep work each week?',
    sliderLabel: 'Weekly focus hours',
    autoPlace: 'Let Cadence place it for me',
    autoPlaceHint: 'Focus blocks are placed around your meetings each morning.',
    gentle: 'Gentle and sustainable.',
    strong: 'Strong, sustainable.',
    ambitious: 'Ambitious. Cadence defends it hard.',
    deep: 'Deep-work heavy.',
  },

  drivers: {
    eyebrow: 'Step 5 \u00b7 What guides your year',
    title: 'Your strategic drivers.',
    sub: 'The few themes that matter most this year. Cadence links each task to one, so you can see whether your time matches your strategy.',
    placeholder: 'Add your own, e.g. Win the NHS framework',
    add: 'Add',
    build: 'Build my first day',
    hint: 'Two to four is plenty.',
    hintClaude: 'You can sharpen them with Claude later.',
    hintOther: 'You can change them any time in Setup.',
  },

  build: {
    eyebrow: 'Almost there',
    title: 'Building your first day.',
    reading: 'Reading your calendar',
    protecting: 'Protecting {hours}h of focus around your meetings',
    schoolRun: 'Holding the school run',
    lunch: 'Holding lunch',
    protectedTime: 'Holding your protected time',
    drivers: 'Lining your work up with your drivers',
    shaping: 'Shaping your day',
    payoffTitle: 'Tomorrow, protected.',
    held: '{hours}h of focus held',
    emptyDay: 'Your day is set, with focus protected around what\u2019s already on your calendar.',
  },

  // Claude's own menu names. One place, so a Claude UI change is one edit
  // (checked against Claude's Help Center, 27 September 2026).
  claudeUi: {
    connectors: 'Customize \u2192 Connectors',
    skills: 'Customize \u2192 Skills',
    uploadSkill: '+ \u2192 Create skill \u2192 Upload a skill',
    capabilities: 'Settings \u2192 Capabilities',
    codeExecution: 'Code execution and file creation',
    scheduled: 'Scheduled',
  },

  // ── Part two: the Dashboard card, the assistant card and the drawers ──
  card: {
    eyebrow: 'Part two \u00b7 in Claude',
    title: 'Let Claude work through your day in the background.',
    sub: 'Four steps in Claude. Cadence shows you exactly what to do and ticks each one off when it sees it working.',
    progress: '{n} of 4 done \u00b7 about {minutes} minutes left',
    upNext: 'Up next',
    minutes: '{n} min',
    notYet: 'not yet',
    ran: 'Ran {time}',
    sweepTimes: '{first} to {last}',
    sweepName: 'Action sweep',
    sweepGives: 'New actions filed, each with a link back.',
    closeName: 'Close the day',
    closeGives: 'Finished work closed, the rest moved to tomorrow.',
    prepareName: 'Prepare tomorrow',
    prepareGives: 'Your executives\u2019 next day, planned before they look.',
    steps: {
      connector: { title: 'Add Cadence to Claude', sub: 'So Claude can read your plan and file work into Cadence.' },
      skills: { title: 'Install the Cadence skills', sub: 'Teaches Claude your sweep and your close.', subEa: 'Teaches Claude your sweep and how to prepare tomorrow.' },
      tools: { title: 'Connect your tools in Claude', sub: '{tools}. Claude reads them, not Cadence.', subNone: 'No tools picked, so nothing to connect.' },
      routines: { title: 'Schedule your routines', sub: 'Your sweep and close, set up as scheduled tasks in Claude. They run in the cloud.', subEa: 'Your sweep and prepare-tomorrow, set up as scheduled tasks in Claude. They run in the cloud.' },
    },
    readyTitle: 'When you\u2019re ready',
    driversTitle: 'Sharpen your drivers with Claude',
    driversSub: 'Claude asks about this year\u2019s objectives, then updates your drivers in Cadence.',
    driversAction: 'Copy prompt',
    driversPrompt: 'Help me sharpen my Cadence strategic drivers for this year. Ask me a few questions about my objectives, then update my drivers in Cadence with get_strategic_drivers and update_strategic_driver.',
    devicesTitle: 'Get Cadence on your devices',
    devicesSub: 'Mac, Windows and iPhone. Your setup carries over.',
    devicesAction: 'See downloads',
    doneTitle: 'Claude is working for you.',
    doneSub: 'Your sweep and close run in the cloud. Cadence tells you if one stops.',
    doneSubEa: 'Your sweep and prepare-tomorrow run in the cloud. Cadence tells you if one stops.',
    stepDone: 'Done',
    stepNotDone: 'Not done yet',
    progressLabel: 'Claude setup progress',
    seeHealth: 'See setup health',
    hide: 'Hide',
    hidden: 'Hidden. You\u2019ll find it in Setup.',
    hideFailed: 'Could not hide the card. Try again.',
    saveFailed: 'Could not save. Try again.',
    copied: 'Copied',
  },

  assistantCard: {
    eyebrow: 'Part two \u00b7 your AI assistant',
    otherTitle: 'Your routines run in Claude, not {assistant} yet.',
    otherSub: 'Cadence is protecting your calendar now. The action sweep and end-of-day close need Claude today. Cadence will tell you when {assistant} is supported.',
    otherSubEa: 'Cadence is working now. The action sweep and preparing tomorrow for your executives need Claude today. Cadence will tell you when {assistant} is supported.',
    notYetTitle: 'Part two needs Claude.',
    notYetSub: 'Claude runs your action sweep and end-of-day close in the background. A Pro plan is enough to start, and setup takes about 10 minutes.',
    notYetSubEa: 'Claude runs your action sweep and prepares tomorrow for your executives in the background. A Pro plan is enough to start, and setup takes about 10 minutes.',
    tellMe: 'Tell me when {assistant} works',
    onList: 'You\u2019re on the list',
    haveClaude: 'I have Claude too',
    haveClaudeNow: 'I have Claude now',
    getClaude: 'Get Claude',
    itNote: 'Copy note asking IT for Claude',
  },

  railTile: {
    label: 'Finish setup',
    count: '{n} of 4',
  },
  railSetup: 'Setup',

  drawer: {
    eyebrow: 'Step {n} of 4 \u00b7 in Claude',
    close: 'Close',
    openClaude: 'Open Claude',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Your browser didn\u2019t let Cadence copy. Select the text below and copy it.',
    copyAdminNote: 'Copy note for your admin',
    copyItNote: 'Copy note for IT',
    troubleTitle: 'Something not working?',
    connector: {
      title: 'Add Cadence to Claude',
      why: 'Claude can then read your plan and file work into Cadence. You approve what it can do, and you can disconnect at any time.',
      work: 'Your company manages Claude. On Team and Enterprise plans an owner usually adds custom connectors for everyone. If you can\u2019t see \u201cAdd custom connector\u201d, send your Claude admin this note.',
      free: 'Claude Free allows one custom connector, so Cadence can be that one.',
      step1: 'Open Claude\u2019s connector settings.',
      step1Sub: 'In Claude, go to {connectors}, then choose +.',
      step2: 'Choose \u201cAdd custom connector\u201d and paste these.',
      name: 'Name',
      url: 'URL',
      oauthBlank: 'Leave the OAuth client ID and secret blank. Cadence sets those up itself.',
      step3: 'Sign in to Cadence, then choose Allow.',
      step3Sub: 'A browser window opens. It closes by itself once you\u2019re connected.',
      trouble: {
        clientId: '\u201cUnknown client_id\u201d when you choose Allow',
        clientIdFix: 'Quit Claude fully, reopen it, remove the Cadence connector and add it again.',
        blank: 'A blank sign-in page',
        blankFix: 'Same fix: remove the connector in Claude and add it again.',
        data: 'Claude can\u2019t see your data',
        dataFix: 'Check you signed in to Cadence with the same account you use here.',
      },
      idle: 'This ticks itself off when Claude connects.',
      live: 'Watching for Claude to connect\u2026',
      done: 'Connected at {time}. Claude has called Cadence.',
      next: 'Next: skills',
    },
    skills: {
      title: 'Install the Cadence skills',
      why: 'Skills are short instruction files. They teach Claude how to run your sweep and your close the Cadence way. They\u2019re the same for everyone: Claude reads your details from Cadence.',
      whyEa: 'Skills are short instruction files. They teach Claude how to run your sweep and prepare tomorrow for your executives the Cadence way. They\u2019re the same for everyone: Claude reads your details from Cadence.',
      download: 'Download',
      work: 'Your Claude owner can switch on code execution and add these skills for everyone, so you can skip this step.',
      step1: 'Switch on code execution.',
      step1Sub: 'In Claude, open {capabilities} and switch on {codeExecution}. Skills need it.',
      step2: 'Upload each skill.',
      step2Sub: 'Open {skills}, choose {uploadSkill}, and upload one file. Repeat for each.',
      step3: 'Check it worked.',
      step3Sub: 'Paste this into a new Claude chat. Claude checks the skills and tells Cadence.',
      idle: 'This ticks itself off after the check.',
      live: 'Waiting for Claude\u2019s setup check\u2026',
      done: 'All 4 skills found at {time}.',
      update: 'Version {version} is ready. Download the skills again and replace them in Claude.',
      next: 'Next: your tools',
    },
    tools: {
      title: 'Connect your tools in Claude',
      why: 'Claude reads your email, chats and recordings. Cadence never does. Only the actions Claude files reach Cadence, each with a link back to the source.',
      yourTools: 'Your tools',
      none: 'You haven\u2019t picked any tools. Add some above, or move on.',
      step1: 'In Claude, open {connectors}.',
      step2: 'Connect each tool below.',
      step3: 'Check them.',
      step3Sub: 'Paste this into Claude. It tries each tool and tells Cadence what it found.',
      needsSkills: 'Install the skills first (step 2), then run the check.',
      checkAgain: 'I\u2019ve fixed it, check again',
      checkAgainSub: 'This copies the check. Paste it into Claude again.',
      failed: 'Claude couldn\u2019t reach {tool}. Connect it in Claude, then run the check again.',
      m365It: 'Microsoft 365 needs a one-off approval from your company\u2019s Microsoft admin, even on your own Claude plan. If Claude says \u201cNeed admin approval\u201d, send IT this.',
      m365Personal: 'Personal Microsoft accounts (outlook.com, hotmail.com) can\u2019t connect.',
      notesFallback: 'No connector for yours? Upload transcripts in Cadence under Transcripts instead.',
      how: {
        m365: 'Find Microsoft 365, choose Connect, and sign in with your work account.',
        google: 'Find Gmail, choose Connect, and sign in.',
        slack: 'Find Slack, choose Connect, and pick your workspace.',
        plaud: 'Find Plaud, choose Connect, and sign in to your Plaud account.',
        notes: 'Find Otter or Fireflies, choose Connect, and sign in.',
        sf: 'Find Salesforce, choose Connect, and sign in. Read access is enough.',
      },
      pill: { ok: 'Found', missing: 'Not found', error: 'Not found', notChecked: 'Not checked', checking: 'Checking' },
      idle: 'This ticks itself off after the check.',
      live: 'Waiting for Claude to check your tools\u2026',
      warn: '{n} not found. Fix them in Claude, then check again.',
      warnOne: '1 not found. Fix it in Claude, then check again.',
      done: 'All {n} found. Claude can read them.',
      doneOne: 'Found. Claude can read it.',
      doneNone: 'Nothing to connect.',
      next: 'Next: routines',
    },
    routines: {
      title: 'Schedule your routines',
      why: 'Claude runs these in the cloud on a schedule, so the work is done before you look. Your computer can be off. The times come from your working hours.',
      work: 'On company plans an admin switches Cowork on. If you can\u2019t see Cowork or Scheduled in Claude, send your admin this note.',
      free: 'Scheduled tasks need Claude Pro, Max, Team or Enterprise. You can set up everything else now.',
      step1: 'In Claude, open {scheduled} in the sidebar, then New task, then Create with Claude.',
      step2: 'Paste this and send it.',
      step3: 'Then do the same with this one.',
      step4: 'That\u2019s it.',
      step4Sub: 'Cadence ticks each routine off when its first run arrives. You\u2019ll find them under {scheduled} in Claude\u2019s sidebar.',
      replacing: 'Replacing your routines? In Claude, delete the old Cadence tasks under {scheduled} first, so they don\u2019t run twice.',
      byHand: 'Set them up by hand instead',
      byHandSub: 'In {scheduled}, choose New task, then Set up manually. Make one Weekdays task for each time below, with this name and prompt.',
      byHandSubDays: 'In {scheduled}, choose New task, then Set up manually. Make one Weekly task for each of your working days ({days}) at each time below, with this name and prompt.',
      whyEa: 'Claude runs these in the cloud on a schedule: your sweep through the day, and tomorrow prepared for your executives each evening. Your computer can be off.',
      pill: { none: 'Not set up', waiting: 'Waiting for first run', ran: 'Ran {time}', late: 'Late', stopped: 'Stopped', timesChanged: 'Old times' },
      idle: '{n} of 2 routines running.',
      live: '{n} of 2 running. Waiting for the first runs\u2026',
      done: 'Both routines are running in the cloud.',
      finish: 'Finish',
    },
  },

  setupPage: {
    eyebrow: 'Preferences',
    title: 'Setup',
    sub: 'Everything you chose when you started. Change any of it here and Cadence re-plans from tomorrow.',
    inCadence: 'In Cadence',
    change: 'Change',
    rows: {
      calendar: 'Calendar',
      assistant: 'AI assistant',
      tools: 'Your tools',
      hours: 'Working hours, days and protected time',
      focus: 'Focus rhythm',
      drivers: 'Strategic drivers',
    },
    calendarConnected: '{provider} \u00b7 connected',
    calendarNone: 'Not connected',
    calendarManage: 'Manage',
    assistantClaude: { personal: 'Claude \u00b7 my own plan', work: 'Claude \u00b7 through my company', free: 'Claude \u00b7 Free', unknown: 'Claude' },
    assistantOther: '{assistant} \u00b7 not supported yet',
    assistantNotYet: 'Not yet',
    assistantNone: 'No AI assistant',
    assistantUnset: 'Not picked yet',
    toolsNone: 'None picked',
    hours: '{start} to {end}',
    weekdays: 'Monday to Friday',
    focus: '{hours}h a week',
    focusAuto: 'placed for you each morning',
    focusManual: 'you place it yourself',
    driversNone: 'None yet',
    inClaude: 'In Claude \u00b7 {n} of 4 done',
    stepDoneAt: 'Done {when}',
    stepDone: 'Done',
    stepNothing: 'Nothing to connect',
    stepUpNext: 'Up next \u00b7 about {n} minutes',
    stepAbout: 'About {n} minutes',
    open: 'Open',
    review: 'Review',
    showCard: 'Show the checklist on my Dashboard',
    cardShown: 'The checklist is back on your Dashboard.',
    health: 'Setup health',
    yourAssistant: 'Your AI assistant',
    noneTitle: 'Not using an AI assistant',
    unsetTitle: 'No AI assistant picked yet',
    unsetSub: 'Pick one to add an action sweep through the day and an end-of-day close. Cadence works without one too.',
    inviteTitle: 'Your EA',
    noneSub: 'Your action sweep and end-of-day close are off. Everything else in Cadence works.',
    noneSubEa: 'Your action sweep and preparing tomorrow are off. Everything else in Cadence works.',
    notYetTitle: 'No assistant yet',
    otherTitle: '{assistant} isn\u2019t supported yet',
    otherSub: 'The action sweep and end-of-day close need Claude today.',
    otherSubEa: 'The action sweep and preparing tomorrow need Claude today.',
    connectAssistant: 'Connect an assistant',
    startAgain: 'Start again',
    rerunTitle: 'Run the whole setup again',
    rerunSub: 'Every step, with your current answers filled in. Each step saves when you continue.',
    rerun: 'Run setup',
    routinesChangedTitle: 'Your routine times changed. Update them in Claude.',
    routinesChangedSub: 'Your scheduled tasks in Claude still run at the old times. In Claude, delete the old Cadence tasks, then paste the new prompts.',
    routinesChangedAction: 'Update routines',
    loadFailed: 'Could not load your setup. Refresh to try again.',
  },

  health: {
    back: 'Setup',
    title: 'Claude setup health',
    sub: 'What Cadence has seen from Claude. It updates every time Claude calls Cadence.',
    issues: '{n} things to check.',
    issue: '1 thing to check.',
    restFine: 'Everything else is running.',
    allFine: 'Everything is running.',
    copyCheck: 'Copy setup check',
    connector: 'Connector',
    connectorTitle: 'Cadence in Claude',
    connectorSeen: 'Last call {when} \u00b7 {n} calls this week',
    connectorNever: 'Claude hasn\u2019t called Cadence yet.',
    connected: 'Connected',
    notConnected: 'Not connected',
    manage: 'Manage',
    connect: 'Connect',
    skills: 'Skills',
    skillsTitle: '{n} Cadence skills \u00b7 version {version}',
    skillsNone: 'Cadence skills',
    skillsUnchecked: 'Not checked yet. Run the setup check in Claude.',
    skillsCurrent: 'Up to date.',
    skillsMissing: 'Claude found {n} of 4. Upload the missing ones.',
    skillsUpdate: 'Version {version} is ready. Download the skills again and replace them in Claude.',
    update: 'Update',
    download: 'Download',
    tools: 'Your tools in Claude',
    toolsNoneTitle: 'No tools picked',
    toolsNoneSub: 'Claude works from your calendar and Cadence only.',
    addTools: 'Add tools',
    toolStale: 'No new items for more than 3 working days. Claude may have lost access.',
    toolFailed: 'Claude couldn\u2019t reach it in the last setup check.',
    toolSeen: 'Last item {when}',
    toolFound: 'Found in the last setup check',
    toolUnchecked: 'Not checked yet',
    working: 'Working',
    check: 'Check',
    notChecked: 'Not checked',
    howToFix: 'How to fix',
    routines: 'Routines in Claude',
    routineStopped: 'No run since {when}. The task may have been paused or deleted in Claude.',
    routineStoppedNever: 'No run yet at its scheduled times. The task may have been paused or deleted in Claude.',
    routineLate: 'Running late. Last run {when}.',
    routineOk: 'Ran {when}',
    routineNext: 'next {when}',
    routineNotSetUp: 'Not set up yet',
    routineTimesChanged: 'Your working hours or days changed, so this still runs at the old times. In Claude, delete the old task, then paste the new prompt.',
    onTime: 'On time',
    late: 'Late',
    stopped: 'Stopped',
    notSetUp: 'Not set up',
    copyFix: 'Copy fix prompt',
    setUp: 'Set up',
    openGuide: 'Open the setup guide',
    today: 'today at {time}',
    yesterday: 'yesterday at {time}',
    tomorrow: 'tomorrow at {time}',
    onDay: '{day} at {time}',
    notClaude: 'Setup health is for Claude. Change your AI assistant in Setup to see it.',
  },

  edit: {
    backToSetup: 'Back to Setup',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Saved. Cadence re-plans from tomorrow.',
    savedNoReplan: 'Saved.',
    savedKept: 'Saved. Tomorrow\u2019s plan was already reviewed, so Cadence left it. The change applies from the next plan.',
    timeZone: 'Time zone',
  },

  invite: {
    rowTitle: 'Invite your EA',
    rowSub: 'They join with their own login, assigned to you, and can prepare your day.',
    copyLink: 'Copy invite link',
    making: 'Making a link\u2026',
    copied: 'Link copied. It works once, for 7 days.',
    shown: 'Your browser didn\u2019t let Cadence copy. Send this link to your EA. It works once, for 7 days.',
    shownOther: 'Your browser didn\u2019t let Cadence copy. Send this link to the EA. It works once, for 7 days.',
    checkFailed: 'Couldn\u2019t check the link just now.',
    tryAgain: 'Try again',
    failed: 'Could not make a link. Try again.',
    // The organisation page
    openTitle: 'Open invite links',
    openSub: 'Each link works once and expires after 7 days.',
    none: 'No open links.',
    forExec: 'For {name}',
    madeBy: 'Made by {name} \u00b7 expires {when}',
    revoke: 'Revoke',
    revoked: 'Link revoked.',
    revokeFailed: 'Could not revoke the link. Try again.',
    newLink: 'New EA invite link',
    pickExec: 'For which executive?',
    make: 'Make link',
    // The join page
    joinEyebrow: 'You\u2019re invited',
    joinTitle: 'Join {organisation} on Cadence',
    joinSub: '{invitedBy} invited you to be {executive} executive assistant.',
    joinSubSelf: '{invitedBy} invited you to be their executive assistant.',
    yourName: 'Your name',
    yourEmail: 'Your work email',
    password: 'Choose a password',
    passwordHint: 'At least 8 characters, with upper and lower case, a number and a symbol.',
    join: 'Join and sign in',
    joining: 'Joining\u2026',
    expires: 'This link expires {when}.',
    gone: 'This invite link has expired, been used or been revoked. Ask for a new one.',
    invalid: 'This invite link isn\u2019t valid. Check you copied all of it.',
    signIn: 'Go to sign in',
    joinedSignIn: 'You\u2019ve joined. Sign in to continue.',
  },

  adminNotes: {
    connector: 'Hi, I\u2019d like to use Cadence (app.stratiumhq.com) with Claude. Could you add it as a custom connector for our Claude organisation?\n\nName: Cadence\nURL: {url}\nOAuth client ID and secret: leave blank\n\nThanks',
    skills: 'Hi, could you switch on code execution for our Claude organisation (in Claude\u2019s admin settings, under Plugins and skills, then Policy) and add the four Cadence skills for everyone? They\u2019re the same for everyone and can be downloaded from Cadence.\n\nThanks',
    cowork: 'Hi, could you switch on Cowork for me in our Claude organisation? I\u2019m setting up scheduled tasks for Cadence.\n\nThanks',
    m365: 'Hi, I\u2019m connecting Claude to my Microsoft 365 account (Outlook, Teams and calendar, read access). Claude says it needs admin approval. Could you approve the Claude app for our Microsoft Entra ID tenant?\n\nThanks',
    claude: 'Hi, could we get Claude (claude.ai) for me? I use Cadence, and its action sweep and end-of-day close run in Claude.\n\nThanks',
  },

  routinePrompt: {
    // One task per time: a scheduled task may take only one time of day.
    createSweep: 'Create a scheduled task for each of these times, {days}: {times} ({zone}). Call each one \u201c{name}\u201d.\n\nEach run: {run}\n\nThen run one of them once now so Cadence can confirm it works.',
    // The close only closes at its time; a test run just checks the setup.
    createOnce: 'Create a scheduled task called \u201c{name}\u201d that runs {days} at {times} ({zone}).\n\nEach run: {run}\n\nThen run it once now as a test run, so Cadence can confirm it works. A test run only checks the setup: it doesn\u2019t close or prepare anything.',
    sweepName: 'Cadence \u00b7 Action sweep',
    sweepRun: 'Run my Cadence inbox sweep since the last run.',
    closeName: 'Cadence \u00b7 Close the day',
    closeRun: 'Run my Cadence end-of-day close.',
    prepareName: 'Cadence \u00b7 Prepare tomorrow',
    prepareRun: 'Run my Cadence end-of-day close to prepare tomorrow for my executives.',
    weekdays: 'on weekdays',
    onDays: 'on {days}',
    checkPrompt: 'Check my Cadence setup.',
  },

  skills: {
    title: 'Cadence skills for Claude',
    intro: 'Four skills that teach Claude your action sweep, your end-of-day close and the Cadence setup check. They\u2019re the same for everyone: Claude reads your hours, tools and time zone from Cadence each time it runs.',
    download: 'Download',
    copy: 'Copy markdown',
    copied: 'Copied markdown',
    items: {
      'cadence-base': 'Start here. How Cadence works, your setup, and the setup check. The others build on it.',
      'cadence-inbox-triage': 'The action sweep. Files new actions from your email, chats, recordings and CRM, each with a link back.',
      'cadence-close-day': 'The end-of-day close. Completes what\u2019s done and moves the rest to your next working day.',
      'cadence-daily-brief': 'On request: a briefing of your day in Claude, with meeting prep saved to Cadence.',
    },
    installTitle: 'How to install',
    installCodeExecution: 'In Claude, open {capabilities} and switch on {codeExecution}. Skills need it.',
    installUpload: 'Open {skills}, choose {uploadSkill}, and upload one file. Repeat for each.',
    installCheck: 'In a new chat, say \u201cCheck my Cadence setup.\u201d',
    installTeams: 'On Team and Enterprise plans, an owner can switch on code execution and add the skills for everyone.',
    installClaudeCode: 'Claude Code: unzip each file into ~/.claude/skills/.',
    needsConnector: 'The skills need the Cadence connector, so add that first.',
  },

  done: {
    title: 'Tomorrow is protected.',
    partOneDone: 'Part one done',
    setupDone: 'Setup done',
    claudeSub: 'Cadence has your calendar, your hours and your focus. Next, connect Claude so the day runs itself.',
    otherSub: 'Cadence has your calendar, your hours and your focus. That works on its own from today.',
    noneSub: 'Cadence has your calendar, your hours and your focus. It re-plans each morning around your meetings.',
    claudeCardTitle: 'Part two: let Claude do the rest',
    claudeCardBody: 'About 10 minutes in Claude. Cadence shows you each step and ticks it off when it sees it working.',
    otherCardTitle: 'Part two runs in Claude, not {assistant} yet',
    otherCardBody: 'Without Claude you still get protected focus time, your plan and your morning brief, but not these two:',
    notYetCardTitle: 'Part two needs Claude',
    notYetCardBody: 'Claude is the assistant that runs your routines in the background. A Pro plan is enough to start, and setup takes about 10 minutes.',
    noneCardTitle: 'Change anything later',
    noneCardBody: 'Everything you chose is in Setup, at the bottom of the sidebar. That’s also where you connect an AI assistant if you want an action sweep through the day and an end-of-day close.',
    noneCardBodyEa: 'Everything you chose is in Setup, at the bottom of the sidebar. That’s also where you connect an AI assistant if you want an action sweep through the day and tomorrow prepared for your executives.',
    sweepLine: 'Action sweep. New actions filed, each with a link back.',
    sweepAgain: 'Again at {times}.',
    closeLine: 'Close the day. Finished work closed, the rest moved to tomorrow.',
    closeLineEa: 'Prepare tomorrow. Your executives\u2019 next day, planned before they look.',
    briefLine: 'Your morning brief still arrives by email at {time}.',
    freeLine: 'The scheduled routines need a paid Claude plan. You can set up the rest now.',
    setUpClaude: 'Set up Claude now',
    later: 'Later, take me to Cadence',
    useClaude: 'I have Claude too, use it',
    setUpWithClaude: 'Set up with Claude',
    tellMe: 'Tell me when {assistant} works',
    goToCadence: 'Go to Cadence',
  },
} as const;
